import { slipKindLabel } from 'utils/slip.utils';
import { EventBet, EventBets, Slip } from 'types/Slip';
import styles from './SlipDetails.module.css';
import { TicketMultiBonus } from 'components/MultiBonus/MultiBonus';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { formatDateTime } from 'helpers/dateHelper';
import { describePick } from 'helpers/pickLabel';
import { PickSummary } from 'components/PickSummary/PickSummary';
import { Dispatch, SetStateAction, useState } from 'react';
import { CURRENCY } from 'App';
import { ReplayRemaining } from 'components/ReplayRemaining/ReplayRemaining';
import { CashoutButton } from 'components/Cashout/CashoutButton';
import { StornoButton } from 'components/Storno/StornoButton';
import { CashoutOffer } from 'services/api/slipService';

type Outcome = 'won' | 'lost' | 'open';

const WON = new Set(['winning', 'won', 'win']);
const LOST = new Set(['failed', 'lost', 'lose']);

const betOutcome = (bet: EventBet): Outcome => {
  const status = String(bet.status ?? '').toLowerCase();
  if (WON.has(status)) return 'won';
  if (LOST.has(status)) return 'lost';
  return 'open';
};

/** An event reads as lost if any of its picks lost, won once every pick won. */
const eventOutcome = (bets: EventBet[]): Outcome => {
  const outcomes = bets.map(betOutcome);
  if (outcomes.includes('lost')) return 'lost';
  if (outcomes.length > 0 && outcomes.every((o) => o === 'won')) return 'won';
  return 'open';
};

const FULL_TIME_RESULT = /^(full ?time result|match result|1x2)$/i;
const norm = (value?: string | null) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

/**
 * 1 / X / 2 for a full-time-result pick. Live picks carry the team name
 * ("Ostersunds FK") rather than the code, so match it against the teams.
 */
const pickCode = (bet: EventBet, event: EventBets['event']) => {
  if (!FULL_TIME_RESULT.test(String(bet.betName ?? '').trim())) return null;
  const name = norm(bet.odd?.name);
  if (!name) return null;
  if (name === '1' || name === norm(event.home)) return '1';
  if (name === '2' || name === norm(event.away)) return '2';
  if (name === 'x' || name === 'draw' || name === 'tie') return 'X';
  return null;
};

const formatScore = (value?: string | null) =>
  String(value ?? '')
    .replace(/-/g, ':')
    .replace(/\s+/g, '')
    .trim();

interface Props {
  slip: Slip;
  setShowSlipDetails: Dispatch<SetStateAction<Slip | null>>;
  cashout?: CashoutOffer;
  onCashedOut?: () => void;
}

export const SlipDetails = ({
  slip,
  setShowSlipDetails,
  cashout,
  onCashedOut = () => {},
}: Props) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(true);
  const slipStatus = String(slip.status ?? '').toLowerCase();

  return (
    <div className={styles.wrapper}>
      <div
        className={styles.closeButton}
        role="button"
        tabIndex={0}
        onClick={() => setShowSlipDetails(null)}
      >
        <div className={styles.arrowBack} />
        {t('close_label')}
      </div>
      <div className={styles.slipHeader}>
        <div>
          <span>{t('bet_slip_label')}:</span>
          <span>{slip.id}</span>
        </div>
        <div className={classNames(styles.slipStatus, styles[slipStatus])}>
          <div />
          {t(`${slipStatus}_label`, { defaultValue: slipStatus })}{' '}
          {slip.awaiting_other_matches && (
            <>({t('awaiting_other_matches_label')}) </>
          )}
          {slip.is_live_ticket && '*LIVE*'}
        </div>
      </div>
      <div className={styles.replayRemaining}>
        <ReplayRemaining slip={slip} />
      </div>
      {(slip.storno_left_ms ?? 0) > 0 && (
        <div className={styles.cashout}>
          <StornoButton slip={slip} onCancelled={onCashedOut} compact />
        </div>
      )}
      {cashout && cashout.reason !== 'storno' && (
        <div className={styles.cashout}>
          <CashoutButton offer={cashout} onCashedOut={onCashedOut} compact />
        </div>
      )}
      <div className={styles.eventsWrapper}>
        {slip.event_bets &&
          slip.event_bets.length > 0 &&
          slip.event_bets.map((x, eventIndex) => {
            const outcome = eventOutcome(x.bets ?? []);
            const scoreAtBet = x.event.isLive
              ? formatScore(x.event.resultAtCreation)
              : '';
            return (
              <div
                key={`${x.event.eventId}-${eventIndex}`}
                className={styles.eventGroup}
                data-outcome={outcome}
              >
                <div className={styles.eventDetails}>
                  <span>
                    {x.event.time > 0
                      ? formatDateTime(new Date(x.event.time * 1000), {
                          removeSeconds: true,
                          removeYear: true,
                        })
                      : ''}
                  </span>
                  <div>
                    <span>{x.event.home}</span>
                    <span>{x.event.away}</span>
                    {x.event.isLive && (
                      <span className={styles.liveAtBet}>
                        <span className={styles.liveTag}>LIVE</span>
                        {scoreAtBet
                          ? t('score_at_bet_label', { score: scoreAtBet })
                          : t('live_bet_label')}
                      </span>
                    )}
                  </div>
                  <div className={styles.eventResult}>
                    <span>{t('result_label')}:</span>
                    <span className={styles.finalScore}>
                      {x.event.result.homeGoals != null &&
                      x.event.result.awayGoals != null
                        ? `${x.event.result.homeGoals}:${x.event.result.awayGoals}`
                        : '-'}
                    </span>
                  </div>
                </div>
                {x.bets &&
                  x.bets.length > 0 &&
                  x.bets.map((y, index) => {
                    const betStatus = String(y.status ?? '').toLowerCase();
                    const code = pickCode(y, x.event);
                    return (
                      <div
                        className={styles.betDetails}
                        data-outcome={betOutcome(y)}
                        key={index}
                      >
                        {x.bets.length > 1 && (
                          <span className={styles.letter}>
                            {String.fromCharCode(index + 65)}
                          </span>
                        )}
                        <span>
                          {code && (
                            <span className={styles.pickCode}>{code}</span>
                          )}
                          <PickSummary
                            info={describePick(
                              {
                                betName: y.betName,
                                odd: y.odd,
                                home: x.event.home,
                                away: x.event.away,
                              },
                              t
                            )}
                            odds={y.odd.odds}
                            match={`${x.event.home} - ${x.event.away}`}
                          />
                        </span>
                        <span
                          className={classNames(
                            styles.betStatus,
                            styles[betStatus]
                          )}
                        >
                          {t(`${betStatus}_label`, {
                            defaultValue: betStatus,
                          })}
                        </span>
                        <span className={styles.odds}>
                          {y.odd.odds.toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
              </div>
            );
          })}
      </div>
      <div className={styles.detailsWrapper}>
        <div
          onClick={() => setShowDetails(!showDetails)}
          className={styles.button}
        >
          <div
            className={classNames(
              styles.icon,
              showDetails ? styles.minusSign : styles.plusSign
            )}
          />
          {t('show_details_label')}
        </div>
        {showDetails && (
          <>
            <div className={styles.details}>
              <span>{t('date_label')}</span>
              <span>
                {formatDateTime(new Date(slip.created_at), {
                  removeSeconds: true,
                  removeYear: true,
                })}
              </span>
            </div>
            <div className={styles.details}>
              <span>{t('description_label')}</span>
              <span>{slipKindLabel(slip, t)}</span>
            </div>
            <div className={styles.details}>
              <span>{t('number_of_events_label')}</span>
              <span>{slip.event_bets.length}</span>
            </div>
            {slip.combination > 1 && (
              <>
                <div className={styles.details}>
                  <span>{t('number_of_bets_label')}</span>
                  <span>{slip.combination}</span>
                </div>
                <div className={styles.details}>
                  <span>{t('stake_per_bet_label')}</span>
                  <span>
                    {(Number(slip.stake) / slip.combination).toFixed(2)}{' '}
                    {CURRENCY}
                  </span>
                </div>
              </>
            )}
            <div className={styles.details}>
              <span>{t('total_stake_label')}</span>
              <span>
                {slip.stake} {CURRENCY}
              </span>
            </div>
            <div className={styles.details}>
              <span>{t('max_coefficient_label')}</span>
              <span>{slip.maxCoefficient.toFixed(2)}</span>
            </div>
            <div className={styles.details}>
              <span>{t('max_gain_label')}</span>
              <span>
                {slip.payment} {CURRENCY}
              </span>
            </div>
            {!!slip.multi_bonus_percent && (
              <div className={styles.details}>
                <span>{t('multi_bonus_label')}</span>
                <TicketMultiBonus
                  percent={slip.multi_bonus_percent}
                  amount={slip.multi_bonus_amount}
                  compact
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
